import { useContext, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import history from '@history';
import { useDownloadBlob, useNativeDebounce, useRequest } from '@main/hooks';
import WdPageSimple from '@main/core/WdPageSimple';
import { styled } from '@mui/material/styles';
import { CircularProgress, Grid, Switch, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {
  BasicSelect,
  ButtonWithExpand,
  ComposedHeader,
  ComposedTable,
  InputSearch,
  Paginator,
} from '@main/components';
import { defaultsEnum } from 'src/app/enums/defaultsEnum';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import Button from '@mui/material/Button';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useSimpleAlert } from '@main/hooks/useSimpleAlert';
import {
  searchRecognitions,
  resetRecognition,
  catalogLocations,
  catalogCompanies,
  statusRecognition,
  exportFile,
  deleteRecognition,
  rebootRecognition,
  syncRecognition,
  catalogBrands,
  catalogModels,
} from 'src/app/api/recognition';
import { useExtractSelector } from '@main/hooks/useExtractSelector';
import { useTerminalState } from '@main/hooks/useTerminalState';
import { showMessage } from 'app/store/core/messageSlice';
import _ from 'lodash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { typeDownloadEnum } from 'src/app/enums/fileEnum';
import { formatDate } from 'src/app/utils/dates';
import PortraitOutlinedIcon from '@mui/icons-material/PortraitOutlined';

import { useTheme } from '@mui/styles';
import { customSwitchStyles } from 'app/styles/styled/customSwitchStyles';

const Root = styled(WdPageSimple)(({ theme }) => ({
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const exportFileOptions = _.omit(typeDownloadEnum, ['txt', 'zip']);

const statusLicensesData = [
  {
    label: 'Todo',
    value: '',
  },
  {
    label: 'Activo',
    value: 'true',
  },
  {
    label: 'Inactivo',
    value: 'false',
  },
];

const tableRowScheme = [
  {
    title: 'Nombre',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Clave',
    minWidth: '320px',
    width: '320px',
  },
  {
    title: 'Descripcion',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Estatus',
    minWidth: '80px',
  },
  {
    title: 'ID Dispositivo',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Modelo',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Marca',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Hostname',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Zona',
    minWidth: '200px',
    maxWidth: '200px',
  },
  {
    title: 'Edificio',
    minWidth: '200px',
    maxWidth: '200px',
  },
  {
    title: 'Locacion',
    minWidth: '200px',
    maxWidth: '200px',
  },
  {
    title: 'Compania',
    minWidth: '200px',
    maxWidth: '200px',
  },
  {
    title: 'Actualizacion',
    minWidth: '120px',
  },
  {
    title: 'Creacion',
    minWidth: '120px',
  },
  {
    title: 'Opciones',
    minWidth: '165px',
    fixedRight: true,
  },
];

const OptionButtons = memo(({ onUpdate, onDelete }) => {
  return (
    <div className="flex w-full flex-wrap flex-row justify-between items-center">
      <Tooltip title="Actualizar informacion de registro de terminal">
        <span>
          <Button
            className="my-3"
            onClick={onUpdate}
            size="small"
            variant="contained"
            color="secondary"
            style={{ width: '90px', justifyContent: 'center' }}
            startIcon={<EditOutlinedIcon />}
          >
            Editar
          </Button>
        </span>
      </Tooltip>

      <Tooltip title="Eliminar informacion de registro de terminal">
        <span>
          <Button
            className="my-3"
            onClick={onDelete}
            size="small"
            variant="contained"
            color="error"
            sx={{ minWidth: '3.2rem', aspectRatio: 1 / 1, padding: '0rem' }}
          >
            <DeleteForeverIcon />
          </Button>
        </span>
      </Tooltip>
    </div>
  );
});

const Terminals = () => {
  const Dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const openAlert = useSimpleAlert();
  useTerminalState({
    url: undefined,
    space: '/administrator/manager/terminal',
    event: 'registers',
    name: 'administrator/manager/terminal',
  });

  const [dataList, setDataList] = useState([]);
  const [querySearch, setQuerySearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(200);
  const [datePickerValue, setDatePickerValue] = useState([null, null]);
  const [form, setForm] = useState({ query: '', initDate: '', endDate: '', paginate: undefined });
  const theme = useTheme();
  const [statusSelect, setStatusSelect] = useState('');
  const [locationSelect, setLocationSelect] = useState('');
  const [companySelect, setCompanySelect] = useState('');
  const [modelSelect, setModelSelect] = useState('');
  const [brandSelect, setBrandSelect] = useState('');
  const [reactiveTableScheme, setReactiveTableScheme] = useState(tableRowScheme);

  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };

  const {
    makeRequest: tryGetData,
    isLoading,
    response,
  } = useRequest(searchRecognitions, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });
  const { makeRequest: tryResetTerminal } = useRequest(resetRecognition);
  const { makeRequest: tryEnableTerminal } = useRequest(statusRecognition);
  const { makeRequest: tryDeleteRecognition } = useRequest(deleteRecognition);
  const { makeRequest: tryRebootRecognition } = useRequest(rebootRecognition);
  const { makeRequest: trySyncRecognition } = useRequest(syncRecognition);

  const { dataSelect: companyDataSelect } = useExtractSelector(catalogCompanies, {
    emptyOption: true,
    label: 'label',
    value: 'value',
  });
  const { dataSelect: locationDataSelect, makeRequest: locationTryRequest } = useExtractSelector(
    catalogLocations,
    {
      autoFetch: false,
      emptyOption: true,
      label: 'label',
      value: 'value',
    }
  );
  const { dataSelect: brandDataSelect, makeRequest: brandTryrequest } = useExtractSelector(
    catalogBrands,
    {
      emptyOptions: true,
      label: 'label',
      value: 'value',
    }
  );
  const { dataSelect: modelDataSelect, makeRequest: modelTryrequest } = useExtractSelector(
    catalogModels,
    {
      emptyOptions: true,
      label: 'label',
      value: 'value',
    }
  );

  const {
    formats,
    tryDownload,
    progress: progressExport,
    isLoading: isLoadingExport,
  } = useDownloadBlob(exportFileOptions, exportFile);

  const { triggerAction } = useNativeDebounce({
    delay: 600,
  });

  const handleRequestTerminals = () => {
    triggerAction().then((isOk) => {
      if (!isOk) return;
      tryGetData(form, currentPage);
    });
  };

  const handleExportFile = async (format) => {
    await tryDownload({
      type: format,
      name: 'Puntos de registro',
    });
  };

  const handleClearAllFilters = () => {
    setQuerySearch('');
    setCurrentPage(1);
    setCurrentItemsPerPage(25);
    setDatePickerValue([null, null]);
    setStatusSelect('');
    setLocationSelect('');
    setCompanySelect('');
    setBrandSelect('');
    setModelSelect('');
    setReactiveTableScheme(tableRowScheme);
  };

  useEffect(() => {
    setForm((current) => {
      return {
        ...current,
        query: querySearch,
        paginate: currentItemsPerPage,
        initDate: datePickerValue[0] === null ? '' : datePickerValue[0],
        endDate: datePickerValue[1] === null ? '' : datePickerValue[1],
        status: statusSelect === '' ? null : statusSelect.toString(),
        location_key: locationSelect === '' ? null : locationSelect.toString(),
        company_key: companySelect === '' ? null : companySelect.toString(),
        brand: brandSelect === '' ? null : brandSelect.toString(),
        modelName: modelSelect === '' ? null : modelSelect.toString(),
      };
    });
  }, [
    querySearch,
    currentItemsPerPage,
    datePickerValue,
    statusSelect,
    locationSelect,
    companySelect,
    brandSelect,
    modelSelect,
  ]);

  useEffect(() => {
    if (!response) return;
    setDataList(response.data);
    setTotalPages(response.last_page);
    setTotalItems(response.total);
    setCurrentPage(response.current_page);
  }, [response]);

  useEffect(() => {
    if (form === undefined) return;
    handleRequestTerminals();
  }, [currentPage, form]);

  useEffect(() => {
    return () => {
      setDataList([]);
    };
  }, []);

  useEffect(() => {
    setLocationSelect('');
    if (companySelect === '') return;
    locationTryRequest({
      company_key: companySelect,
    });
  }, [companySelect]);

  useEffect(() => {
    setBrandSelect('');
    if (companySelect === undefined || companySelect.trim() === '') return;
    brandTryrequest(companySelect);
  }, [companySelect]);

  useEffect(() => {
    setBrandSelect('');
    if (companySelect === undefined || modelSelect.trim() === '') return;
    modelTryrequest(modelSelect);
  }, [modelSelect]);

  const handleSearchQuery = (query = '') => {
    if (!query.length > 3) return;
    setQuerySearch(query);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangeItems = (items) => {
    setCurrentItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    history.push('/recognition/create');
  };

  const handleUpdate = (id) => {
    history.push(`/recognition/update?id=${id}`);
  };

  const handleEnable = (terminalId, terminalStatus) => {
    const builtForm = {
      status: !terminalStatus,
    };
    openAlert({
      title: 'Atencion',
      description: terminalStatus
        ? 'Esta seguro que desea deshabilitar la terminal?'
        : 'Esta seguro que desea habilitar la terminal?',
    }).then((userResponse) => {
      if (!userResponse) return;
      tryEnableTerminal(terminalId, builtForm).then(({ isOk }) => {
        if (isOk) {
          Dispatch(
            showMessage({
              message: terminalStatus ? 'Terminal deshabilitada' : 'Terminal habilitada',
              autoHideDuration: 4000,
              variant: 'success',
            })
          );
          tryGetData(form);
        }
      });
    });
  };

  const handleReset = (id) => {
    openAlert({
      title: 'Atencion',
      description: 'Esta seguro que desea restablecer la terminal?',
    }).then((userResponse) => {
      if (!userResponse) return;
      tryResetTerminal(id).then(({ isOk }) => {
        if (isOk) {
          Dispatch(
            showMessage({
              message: 'Terminal restaurada',
              autoHideDuration: 4000,
              variant: 'success',
            })
          );
          tryGetData(form);
        }
      });
    });
  };

  const handleDelete = (id) => {
    openAlert({
      title: 'Atencion',
      description: 'Esta seguro que desea eliminar la terminal?',
    }).then((userResponse) => {
      if (!userResponse) return;
      tryDeleteRecognition(id)
        .then(({ isOk }) => {
          if (isOk) {
            Dispatch(
              showMessage({
                message: 'Terminal de reconocimiento eliminada correctamente',
                autoHideDuration: 3000,
              })
            );
            tryGetData(form);
          }
        })
        .catch((error) => console.log(error));
    });
  };

  const handleReboot = (id) => {
    openAlert({
      title: 'Atencion',
      description: 'Esta seguro que desea reiniciar la terminal?',
    }).then((userResponse) => {
      if (!userResponse) return;
      tryRebootRecognition(id)
        .then(({ isOk }) => {
          if (isOk) {
            Dispatch(
              showMessage({
                message: 'Terminal de reconocimiento reiniciada correctamente',
                autoHideDuration: 3000,
              })
            );
            tryGetData(form);
          }
        })
        .catch((error) => console.log(error));
    });
  };

  const handleSync = (id) => {
    openAlert({
      title: 'Atencion',
      description: 'Esta seguro que desea sincronizar la terminal?',
    }).then((userResponse) => {
      if (!userResponse) return;
      trySyncRecognition(id)
        .then(({ isOk }) => {
          if (isOk) {
            Dispatch(
              showMessage({
                message: 'Terminal de reconocimiento se sincronizo correctamente',
                autoHideDuration: 3000,
              })
            );
            tryGetData(form);
          }
        })
        .catch((error) => console.log(error));
    });
  };

  const handleInfo = (id) => {
    history.push(`/recognition/info?id=${id}`);
  };

  return (
    <Root
      header={
        <ComposedHeader
          title="Terminales de Reconocimiento"
          description="Informacion general de dispositivos de reconocimiento"
        >
          <div className="px-4 py-4">
            <Button
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="primary"
              onClick={() => handleCreate()}
              startIcon={<PortraitOutlinedIcon />}
            >
              Agregar terminal
            </Button>
          </div>
          <div className="px-4 py-4">
            <ButtonWithExpand
              title={!isLoadingExport ? 'Descargar' : `Descargando ${progressExport}%`}
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="success"
              startIcon={
                isLoadingExport ? (
                  <CircularProgress
                    color="inherit"
                    size="1.8rem"
                    variant={progressExport > 1 ? 'determinate' : 'indeterminate'}
                    value={progressExport}
                  />
                ) : (
                  <SimCardDownloadOutlinedIcon />
                )
              }
            >
              {formats.map((option, index) => (
                <ButtonWithExpand.Item
                  disabled={isLoadingExport}
                  key={`${index}_${option.value}_doc`}
                  text={`Documento ${option.label}`}
                  onClick={() => handleExportFile(option.value)}
                />
              ))}
            </ButtonWithExpand>
          </div>
        </ComposedHeader>
      }
      content={
        <Grid container className="px-32 pb-32 pt-0 flex flex-row w-full justify-center">
          <Grid item xs={12} md={12} className="flex w-full">
            <Card className="pb-24 px-24 mt-16 w-full">
              <CardContent className="flex w-full h-full flex-col justify-between">
                <div className="flex flex-col mt-24">
                  <Grid container className="w-full flex justify-center items-center py-8 mb-28">
                    <Grid item xs={12} md={7}>
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="ml-4 mr-8 my-2 w-8/12 md:my-0">
                          <InputSearch
                            value={querySearch}
                            onChange={(query) => handleSearchQuery(query)}
                          />
                        </div>
                        <div className="ml-4 my-2 md:my-0">
                          <Button
                            fullWidth
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={() => handleClearAllFilters()}
                          >
                            Limpiar filtros
                          </Button>
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <div className="flex flex-row justify-center md:justify-end items-center md:mt-0 mt-8">
                        <DateRangePicker
                          slotProps={{ textField: { size: 'small' } }}
                          localeText={{ start: 'Fecha inicial', end: 'Fecha final' }}
                          value={datePickerValue}
                          onChange={(current) => setDatePickerValue(current)}
                          disableFuture
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} className="p-2">
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row flex-wrap md:justify-start justify-around items-center">
                        <div className="px-4">
                          <BasicSelect
                            data={companyDataSelect}
                            onChange={(data) => setCompanySelect(data)}
                            title="Companias"
                            value={companySelect}
                            size="small"
                            staticWidth="12rem"
                          />
                        </div>
                        <div className="px-4">
                          <BasicSelect
                            data={locationDataSelect}
                            onChange={(data) => setLocationSelect(data)}
                            title="Ubicacion"
                            value={locationSelect}
                            size="small"
                            staticWidth="12rem"
                            disabled={companySelect === ''}
                          />
                        </div>
                        <div className="px-4">
                          <BasicSelect
                            data={statusLicensesData}
                            onChange={(data) => setStatusSelect(data)}
                            title="Estatus"
                            value={statusSelect}
                            size="small"
                            staticWidth="12rem"
                          />
                        </div>
                        <div className="px-4">
                          <BasicSelect
                            data={modelDataSelect}
                            onChange={(data) => setBrandSelect(data)}
                            title="Modelo"
                            value={brandSelect}
                            size="small"
                            staticWidth="12rem"
                          />
                        </div>
                        <div className="px-4">
                          <BasicSelect
                            data={brandDataSelect}
                            onChange={(data) => setBrandSelect(data)}
                            title="Marca"
                            value={brandSelect}
                            size="small"
                            staticWidth="12rem"
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row justify-end">
                        <Paginator
                          totalPages={totalPages}
                          totalItems={totalItems}
                          onChangeCurrentPage={(page) => handleChangePage(page)}
                          onChangeItemsPerPage={(items) => handleChangeItems(items)}
                          currentPage={currentPage}
                          currentItemsPerPage={currentItemsPerPage}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <div className="flex flex-col justify-center">
                    <ComposedTable
                      isLoading={isLoading}
                      className="w-full"
                      headers={reactiveTableScheme}
                      data={dataList}
                    >
                      <ComposedTable.Column
                        content={({ id, name }) => (
                          <Tooltip title={id}>
                            <Button
                              size="small"
                              className="font-normal transition-colors duration-100 ease-in-out hover:text-blue-500"
                              onClick={() => handleInfo(id)}
                            >
                              {name}
                            </Button>
                          </Tooltip>
                        )}
                      />
                      <ComposedTable.Column content={({ key }) => key} />
                      <ComposedTable.Column content={({ description }) => description} />
                      <ComposedTable.Column
                        content={({ id, status }) =>
                          id ? (
                            <Switch
                              checked={status}
                              inputProps={{
                                'aria-label': 'Estatus',
                              }}
                              sx={customSwitchStyles(theme)}
                              onChange={() => handleEnable(id, status)}
                            />
                          ) : (
                            defaultsEnum.labels.table
                          )
                        }
                      />
                      <ComposedTable.Column content={({ deviceId }) => deviceId || 'No asociado'} />
                      <ComposedTable.Column content={({ model }) => model || 'No asociado'} />
                      <ComposedTable.Column content={({ brand }) => brand || 'No asociado'} />
                      <ComposedTable.Column content={({ hostname }) => hostname || 'No asociado'} />
                      <ComposedTable.Column
                        content={({ zoneName, zone_key: zoneKey }) =>
                          zoneName || zoneKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column
                        content={({ buildingName, building_key: buildingKey }) =>
                          buildingName || buildingKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column
                        content={({ locationName, location_key: locationKey }) =>
                          locationName || locationKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column
                        content={({ companyName, company_key: companyKey }) =>
                          companyName || companyKey || 'No asociado'
                        }
                      />
                      <ComposedTable.Column content={(item) => formatDate(item.updated_at)} />
                      <ComposedTable.Column content={(item) => formatDate(item.created_at)} />
                      <ComposedTable.Column
                        fixedRight
                        content={({ id }) => (
                          <OptionButtons
                            onUpdate={() => handleUpdate(id)}
                            onDelete={() => handleDelete(id)}
                          />
                        )}
                      />
                    </ComposedTable>
                  </div>
                </div>
                <Grid container spacing={1} className="p-2 mt-24 flex">
                  <Grid item xs={12} md={12} className="py-8">
                    <div className="flex flex-row justify-end">
                      <Paginator
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onChangeCurrentPage={(page) => handleChangePage(page)}
                        onChangeItemsPerPage={(items) => handleChangeItems(items)}
                        currentPage={currentPage}
                        currentItemsPerPage={currentItemsPerPage}
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      }
    />
  );
};

export default Terminals;
